import { Input, MessageBox, SearchIcon, Table } from "@jutro/ui";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { dateLabelMap } from "components/new/MedicalDocumentation/tools";
import { DoctorMSMedicalRecordEntity } from "lib/graphql/megaSchema";
import { isDataTile } from "lib/tools/createWorkspace/tools";
import { DataTile, TileColumn } from "lib/tools/createWorkspace/types";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";

type Props = {
  data: DoctorMSMedicalRecordEntity[];
  chosenMedicalRecords: DoctorMSMedicalRecordEntity[];
  selectedMedicalRecords: DoctorMSMedicalRecordEntity[];
  setSelectedMedicalRecords: Dispatch<
    SetStateAction<DoctorMSMedicalRecordEntity[]>
  >;
};

export const SelectMedicalDocumentationTable = ({
  data,
  chosenMedicalRecords,
  selectedMedicalRecords,
  setSelectedMedicalRecords,
}: Props) => {
  const { addTile, removeTile, visibleTiles } = useTileSystem();
  const [searchText, setSearchText] = useState("");
  const [openedRow, setOpenedRow] =
    useState<DoctorMSMedicalRecordEntity | null>(null);

  useEffect(() => {
    const currentFilePreview = visibleTiles.find(
      (tile) => isDataTile(tile) && tile.name === "file-preview",
    );

    if (!currentFilePreview) {
      setOpenedRow(null);
    }
  }, [visibleTiles]);

  useEffect(() => {
    if (!openedRow) return;

    const currentFilePreviewIndex = visibleTiles.findIndex(
      (tile) => isDataTile(tile) && tile.name === "file-preview",
    );

    if (currentFilePreviewIndex >= 0) {
      removeTile((visibleTiles[currentFilePreviewIndex] as DataTile).id);
    }

    addTile(
      {
        name: "file-preview",
        data: { fileId: openedRow.fileId },
        id: openedRow.fileId,
      },
      ({ visibleTiles }) =>
        visibleTiles.includes("visit-procedure-panel") ||
        currentFilePreviewIndex === 0
          ? TileColumn.Left
          : TileColumn.Right,
    );
  }, [openedRow]);

  const columns = useMemo<ColumnDef<DoctorMSMedicalRecordEntity>[]>(
    () => [
      {
        header: "Nazwa pliku",
        cell: ({ row }) => {
          return (
            <span
              className={`font-paragraph-1 inline-block h-full w-40 cursor-default truncate text-ellipsis align-middle text-jutro-new-warm-gray-800 xl:w-52 2xl:w-64`}
              dangerouslySetInnerHTML={{
                __html: searchText
                  ? (
                      row.original.tidyDocumentationResult?.name ??
                      row.original.name
                    ).replace(
                      new RegExp(searchText, "gi"),
                      (s) => `<span class="bg-jutro-new-blue-300">${s}</span>`,
                    )
                  : (row.original.tidyDocumentationResult?.name ??
                    row.original.name),
              }}
            />
          );
        },
      },
      {
        header: "Data załączenia",
        cell: ({ row }) => (
          <div className="font-paragraph-1 pt-0.5">
            {dayjs(row.original.createdAt).format("YYYY‑MM‑DD")}
          </div>
        ),
      },
    ],
    [searchText],
  );

  const filteredMedicalRecords = useMemo(() => {
    return data
      .filter(
        (md) =>
          !md.tagId || chosenMedicalRecords.find((ch) => ch.tagId === md.tagId),
      )
      .filter((md) =>
        (md.tidyDocumentationResult?.name ?? md.name)
          .toLowerCase()
          .includes(searchText.toLowerCase()),
      );
  }, [data, searchText]);

  const enhancedFilteredMedicalRecords = filteredMedicalRecords.map((row) => {
    return {
      ...row,
      tooltip: (
        <div className="flex max-h-[488px] max-w-[552px] flex-col gap-3 overflow-scroll rounded-lg p-4 text-jutro-new-warm-gray-800">
          <div className="font-heading-4">
            {row.tidyDocumentationResult?.name ?? row.name}
          </div>

          <div>
            <div className="font-paragraph-2">
              {row.tidyDocumentationResult?.type &&
              row.tidyDocumentationResult.type in dateLabelMap
                ? dateLabelMap[row.tidyDocumentationResult.type]
                : "Data"}
            </div>
            <div className="font-paragraph-1">
              {dayjs(
                row.tidyDocumentationResult?.issuedAt ?? row.createdAt,
              ).format("YYYY-MM-DD")}
            </div>
          </div>

          {row.tidyDocumentationResult?.summary &&
            row.tidyDocumentationResult.summary.length > 0 && (
              <div className="flex flex-col gap-3">
                {row.tidyDocumentationResult.summary.map(
                  (e: { header: string; content: string }) => (
                    <div key={e.content}>
                      <div className="font-paragraph-2">{e.header}</div>
                      <div className="font-paragraph-1">{e.content}</div>
                    </div>
                  ),
                )}
              </div>
            )}
        </div>
      ),
    };
  });

  return (
    <div className="flex flex-col gap-4">
      <Input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sideElement={<SearchIcon size="sm" />}
        placeholder="Przeszukaj załączone pliki"
        sideElementPosition="start"
      />

      {filteredMedicalRecords.length > 0 ? (
        <Table
          columns={columns}
          data={enhancedFilteredMedicalRecords}
          size="condensed"
          getIsRowLoading={(row) => row.tidy_status === "INPROGRESS"}
          selectionRowMode={"multiple"}
          selectedRows={selectedMedicalRecords}
          setSelectedRows={setSelectedMedicalRecords}
          withOpen
          openedRow={openedRow}
          setOpenedRow={setOpenedRow}
          selectedRowsHeader={null}
        />
      ) : (
        <MessageBox text="Brak plików" color="warmGray" />
      )}
    </div>
  );
};
